import React, { useRef, useState } from "react";
import { useMotionValueEvent, useScroll } from "framer-motion";

import { css, keyframes } from "@emotion/css";

import { desktop, laptop, mobile, tablet } from "../../../styles/breakpoints";
import { useIsMobile } from "./../../../components/helpers/ScreenCheck";

import AutumnBrunch from "../../../img/png/tech/Autumn Brunch.png";
import Beach from "../../../img/png/tech/Beach.png";
import BGClothes from "../../../img/png/tech/bg-clothes.png";
import BusinessCasual from "../../../img/png/tech/Business Casual.png";
import CasualChic from "../../../img/png/tech/Casual chic.png";
import Elegant from "../../../img/png/tech/Elegant.png";
import EveningGlam from "../../../img/png/tech/Evening glam.png";
import SmartCasual from "../../../img/png/tech/Smart Casual.png";
import Streetwear from "../../../img/png/tech/Streetwear.png";
import { useI18n } from "../../../i18n";

const DressUp = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const [slide, setSlide] = useState(1);
  const isTablet = useIsMobile(744);
  const isLaptop = useIsMobile(1023);
  const overlayHeight = isTablet ? 2000 : isLaptop ? 2500 : 3500;

  useMotionValueEvent(
    scrollY,
    "change",
    (latest) => {
      if (!ref.current?.offsetParent) return;
      const value =
        (100 * (latest - ref.current.offsetParent.offsetTop)) / overlayHeight;

      if (value > 70) {
        setSlide(8);
      } else if (value > 60) {
        setSlide(7);
      } else if (value > 50) {
        setSlide(6);
      } else if (value > 40) {
        setSlide(5);
      } else if (value > 30) {
        setSlide(4);
      } else if (value > 20) {
        setSlide(3);
      } else if (value > 10) {
        setSlide(2);
      } else {
        setSlide(1);
      }
    },
    []
  );

  return (
    <div style={{ position: "relative", backgroundColor: "#000" }}>
      <div className={`${style.container} slide${slide}`}>
        <div className="heading">
          <img src={BGClothes} alt="" className="bg" />
          <h3>{t("tech_dressup_slay")}</h3>
          <h2>{t("tech_dressup_occasion")}</h2>
        </div>
        <div className="content">
          <img src={AutumnBrunch} className="autumn" alt="" />
          <img src={Beach} className="beach" alt="" />
          <img src={BusinessCasual} className="business" alt="" />
          <img src={CasualChic} className="chic" alt="" />
          <img src={Elegant} className="elegant" alt="" />
          <img src={EveningGlam} className="glam" alt="" />
          <img src={SmartCasual} className="smart" alt="" />
          <img src={Streetwear} className="street" alt="" />
        </div>
      </div>
      <div
        tabIndex={1}
        ref={ref}
        className={style.overlay(overlayHeight)}
      ></div>
    </div>
  );
};

const bounce = (bounce = 15, reverse = false) =>
  keyframes({
    "0%": {
      transform: `translateY(0px)`,
    },
    "40%": {
      transform: `translateY(${reverse ? "-" : ""}${bounce}px)`,
    },
    "100%": {
      transform: `translateY(0px)`,
    },
  });

const style = {
  overlay: (overlayHeight) =>
    css({
      width: "100%",
      height: overlayHeight,
    }),
  container: css({
    height: "100vh",
    display: "flex",
    maxWidth: 1830,
    margin: "auto",
    position: "sticky",
    overflow: "hidden",
    zIndex: 1,
    top: 0,
    background: "#000",

    ".heading, .content": {
      position: "absolute",
      marginLeft: "auto",
      left: 0,
      marginRight: "auto",
      right: 0,
      maxWidth: 1400,
      width: "100%",
      transition: "all 2s",
      animationName: `${bounce()}`,
    },
    ".heading": {
      img: {
        position: "absolute",
        margin: "auto",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      },

      h2: {
        background:
          "linear-gradient(90deg, #FFF -3.7%, #DBB8FF 46.8%, #FFF 104.91%)",
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        fontFamily: "Syne",
        fontSize: 170,
        fontStyle: "normal",
        fontWeight: 700,
        lineHeight: "196.336px",
        textAlign: "center",
      },
      h3: {
        color: "#FFF",
        fontFamily: "Syne",
        fontSize: 50,
        fontStyle: "normal",
        fontWeight: 700,
        lineHeight: "65.664px",
        opacity: 0.4,
        textAlign: "center",
      },
    },
    ".content": {
      top: "40%",
      height: 1800,

      animationDelay: "1s",
      animationDuration: "2s",

      img: {
        width: "auto",
        height: "auto",
        position: "absolute",
      },
      ".smart": {
        left: "5%",
        top: 0,
      },
      ".business": {
        right: "5%",
        top: "10%",
      },
      ".chic": {
        top: "7%",
        left: 0,
        right: 0,
        margin: "auto",
      },
      ".autumn": {
        left: "-2%",
        top: "23%",
      },
      ".beach": {
        right: "25%",
        bottom: "17%",
      },
      ".elegant": {
        right: "-10%",
        bottom: "10%",
      },
      ".glam": {
        left: "20%",
        bottom: "10%",
      },
      ".street": {
        left: "-10%",
        bottom: "0",
      },
    },
    "&.slide1": {
      ".content": {
        top: "10%",
      },
      ".heading": {
        top: "45vh",
      },
    },
    "&.slide2": {
      ".content": {
        top: "-10%",
      },
      ".heading": {
        top: "40vh",
      },
    },
    "&.slide3": {
      ".content": {
        top: "-30%",
      },
      ".heading": {
        top: "35vh",
      },
    },
    "&.slide4": {
      ".content": {
        top: "-50%",
      },
      ".heading": {
        top: "20vh",
      },
    },
    "&.slide5": {
      ".content": {
        top: "-70%",
      },
      ".heading": {
        top: "10vh",
      },
    },
    "&.slide6": {
      ".content": {
        top: "-90%",
      },
      ".heading": {
        top: "0vh",
      },
    },
    "&.slide7": {
      ".content": {
        top: "-110%",
      },
      ".heading": {
        top: "-10vh",
      },
    },
    "&.slide8": {
      ".content": {
        top: "-130%",
      },
      ".heading": {
        top: "-20vh",
      },
    },
    [desktop]: {
      ".content": {
        height: 1750,
        img: {
          transform: "scale(0.9)",
        },
      },
      ".heading": {
        h2: {
          fontSize: 120,
          lineHeight: "130px",
        },
        h3: {
          fontSize: 30,
          lineHeight: "40px",
        },
      },
    },
    [laptop]: {
      ".content": {
        height: 1700,
        img: {
        transform: "scale(0.8)",
        }
      },
      ".heading": {
        h2: {
          fontSize: 100,
          lineHeight: "110px",
        },
        h3: {
          fontSize: 25,
          lineHeight: "30px",
        },
      },
    },
    [tablet]: {
      ".content": {
        height: '190vh',
        width: '140%',
        left: '-20%',
        img: {
        transform: "scale(0.5)",
        }
      },

      ".heading": {
        marginTop: '25%',
        h2: {
          fontSize: 55,
          lineHeight: "55px",
        },
        h3: {
          fontSize: 25,
          lineHeight: "30px",
        },
      },
    },
    [mobile]: {
      ".content": {
        height: '200vh',
        width: '150%',
        left: '-20%',

        img: {
          transform: "scale(0.30)",
        },
      },

      ".heading": {
        marginTop: '25%',
        h2: {
          fontSize: 40,
          lineHeight: "50px",
        },
        h3: {
          fontSize: 18,
          lineHeight: "22px",
        },
      },
    },
  }),
};

export default DressUp;
